// Push Notification Utility
import { API_LINK } from '../apiLink/apiLink';
const VAPID_PUBLIC_KEY = 'BColo6B1yyTqHQsQ7RCN4LNRyVwZM9u8k4jghbammd-rs0WK6ryU57Qv3UhJQYrr4qUjhfP1Fw65bnIrNCelJzk';


// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// Request notification permission
export async function requestNotificationPermission() {
    if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return false;
    }

    if (!('serviceWorker' in navigator)) {
        console.log('Service Worker not supported');
        return false;
    }

    if (!('PushManager' in window)) {
        console.log('Push messaging not supported');
        return false;
    }

    try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
    }
}

// Subscribe to push notifications
export async function subscribeToPushNotifications() {
    try {
        // Wait for service worker to be ready
        const registration = await navigator.serviceWorker.ready;

        // Check if already subscribed
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
            // Subscribe to push notifications
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });
        }

        return subscription;
    } catch (error) {
        console.error('Error subscribing to push notifications:', error);
        throw error;
    }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPushNotifications() {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error unsubscribing from push notifications:', error);
        return false;
    }
}

// Send subscription to server
export const sendSubscriptionToServer = async (subscription) => {
    try {
        console.log('📤 Sending subscription to server...');

        // Get user's IP address (will be captured by backend from request)
        const response = await fetch(`${API_LINK}/api/auth/register-push-subscription`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                subscription: subscription,
                userAgent: navigator.userAgent,
                isAnonymous: true // Flag to indicate this is from landing page
            })
        });

        const result = await response.json();

        if (result.success) {
            console.log('✅ Subscription saved to server successfully');
            return { success: true };
        } else {
            console.error('❌ Failed to save subscription:', result.message);
            return { success: false, message: result.message };
        }
    } catch (error) {
        console.error('❌ Error sending subscription to server:', error);
        return { success: false, message: error.message };
    }
};

// Remove subscription from server
export async function removeSubscriptionFromServer() {
    try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        const response = await fetch(`${API_LINK}/api/auth/unregister-push-subscription`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to remove subscription from server');
        }

        return await response.json();
    } catch (error) {
        console.error('Error removing subscription from server:', error);
        throw error;
    }
}

// Initialize push notifications (call this after user login)
export async function initializePushNotifications() {
    try {
        console.log('🔔 Initializing push notifications...');

        // Check if notifications are supported
        if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.log('❌ Push notifications not supported in this browser');
            return { success: false, message: 'Push notifications not supported' };
        }

        // Check current permission
        let permission = Notification.permission;
        console.log('🔔 Current permission:', permission);

        // If permission not granted, request it
        if (permission === 'default') {
            const granted = await requestNotificationPermission();
            if (!granted) {
                return { success: false, message: 'Notification permission denied' };
            }
        } else if (permission === 'denied') {
            return { success: false, message: 'Notification permission denied' };
        }

        console.log('🔔 Waiting for service worker to be ready...');

        // Wait for service worker to be ready with timeout
        const registration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Service worker timeout')), 10000)
            )
        ]);

        console.log('✅ Service worker is ready');

        // Additional wait to ensure service worker is fully active
        if (registration.active) {
            console.log('✅ Service worker is active');
        } else {
            console.log('⏳ Waiting for service worker to activate...');
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        console.log('🔔 Subscribing to push notifications...');

        // Subscribe to push notifications
        const subscription = await subscribeToPushNotifications();
        console.log('✅ Successfully subscribed to push notifications');

        console.log('🔔 Sending subscription to server...');

        // Send subscription to server
        const result = await sendSubscriptionToServer(subscription);

        if (result.success) {
            console.log('✅ Push notifications enabled successfully!');
            return { success: true, message: 'Push notifications enabled successfully' };
        } else {
            console.error('❌ Failed to save subscription to server');
            return { success: false, message: result.message };
        }
    } catch (error) {
        console.error('❌ Error initializing push notifications:', error);
        return { success: false, message: error.message };
    }
}

// Check if notifications are enabled
export function areNotificationsEnabled() {
    return Notification.permission === 'granted';
}

// Get notification permission status
export function getNotificationPermission() {
    if (!('Notification' in window)) {
        return 'unsupported';
    }
    return Notification.permission;
}
